/**
 * Глобальний реєстр ядра системи
 *
 * Цей модуль розв'язує проблему єдиного доступу до екземпляру Kernel
 * з різних частин додатку (Core, Ship, Containers) без порушення
 * архітектурних принципів (Core не повинен знати про Ship).
 *
 * Принципи роботи:
 * - Core використовує базове Kernel за замовчуванням
 * - Ship може перевизначити ядро через registerKernel()
 * - Усі частини додатку отримують актуальний екземпляр через getKernel()
 * - Ініціалізація відбувається в entry points (client/server/root)
 */

import { loggerManager } from '../Managers'
import type { ServiceMap, StoreMap } from '../Contracts/Kernel/IKernelTypes'

interface KernelLike {
    initialize(): Promise<void>
    isKernelInitialized?(): boolean
    getInstance?(): any
    getService?<T = unknown>(name: string, strict?: boolean): T | undefined
    getStore?<T = unknown>(name: string, strict?: boolean): T | undefined
    getLoader?(name: string): any
    getConfig?(): any
    getRoutes?(): any
    registerService?(name: string, service: unknown): void
    registerStore?(name: string, store: unknown): void
    getAllServices?(): ServiceMap
    getAllStores?(): StoreMap
    createApplicationProvider?(): React.ComponentType<{ children: React.ReactNode }>
    setLayoutStore?(store: any): void
    getLayoutStore?(): any
}

/**
 * Символ для зберігання реєстру в window об'єкті
 * Використовується для збереження стану під час HMR
 */
const KERNEL_REGISTRY_SYMBOL = '__FRONTEND_KERNEL_REGISTRY__' as const

/**
 * Глобальне сховище екземпляру ядра
 */
class KernelRegistry {
    private static instance: KernelRegistry
    private kernel: KernelLike | null = null
    private isRegistered = false
    private layoutStore: any = null

    private constructor() {}

    static getInstance(): KernelRegistry {
        // В development режимі використовуємо window для збереження стану під час HMR
        if (typeof window !== 'undefined' && import.meta.env?.DEV) {
            const existingRegistry = (window as any)[KERNEL_REGISTRY_SYMBOL] as KernelRegistry | undefined
            if (existingRegistry) {
                // Оновлюємо статичну змінну для консистентності
                KernelRegistry.instance = existingRegistry
                return existingRegistry
            }
        }

        if (!KernelRegistry.instance) {
            KernelRegistry.instance = new KernelRegistry()
            
            // Зберігаємо в window для збереження під час HMR
            if (typeof window !== 'undefined' && import.meta.env?.DEV) {
                (window as any)[KERNEL_REGISTRY_SYMBOL] = KernelRegistry.instance
            }
        }
        return KernelRegistry.instance
    }

    /**
     * Реєстрація екземпляру ядра
     * Має викликатися в entry points (client/server)
     */
    registerKernel(kernel: KernelLike): void {
        if (this.isRegistered && this.kernel) {
            loggerManager.warn('Kernel вже зареєстровано в глобальному реєстрі, виконуємо cleanup', 'kernel-registry')
            this.cleanupKernel()
        }

        this.kernel = kernel
        this.isRegistered = true

        // Додаємо kernel в глобальний контекст для налагодження
        if (typeof window !== 'undefined' && import.meta.env?.DEV) {
            window.__FRONTEND_KERNEL__ = kernel
            this.setupHMRCleanup()
        }

        loggerManager.info('Kernel успішно зареєстровано в глобальному реєстрі', 'kernel-registry')
    }

    /**
     * Налаштування cleanup при HMR
     */
    private setupHMRCleanup(): void {
        if (import.meta.hot) {
            import.meta.hot.dispose(() => {
                loggerManager.info('HMR: Очищення KernelRegistry', 'kernel-registry')
                this.cleanupKernel()
            })
        }
    }

    /**
     * Cleanup старого kernel при заміні
     */
    private cleanupKernel(): void {
        try {
            if (this.kernel && typeof (this.kernel as any).cleanup === 'function') {
                (this.kernel as any).cleanup()
            }
            loggerManager.debug('Kernel cleanup виконано', 'kernel-registry')
        } catch (error) {
            loggerManager.warn('Помилка при cleanup kernel', 'kernel-registry', undefined, { error })
        }
    }

    /**
     * Отримання екземпляру ядра
     * Використовується в усіх частинах додатку
     */
    getKernel(): KernelLike | null {
        return this.kernel
    }

    /**
     * Перевірка реєстрації ядра
     */
    isKernelRegistered(): boolean {
        return this.isRegistered && this.kernel !== null
    }

    /**
     * Скидання реєстру (для тестів)
     */
    reset(): void {
        this.kernel = null
        this.isRegistered = false
        this.layoutStore = null
        
        // Очищуємо з window під час скидання
        if (typeof window !== 'undefined') {
            delete (window as any)[KERNEL_REGISTRY_SYMBOL]
        }
    }

    setLayoutStore(store: any): void {
        this.layoutStore = store
    }

    getLayoutStore(): any {
        return this.layoutStore
    }
}

// Экспорт singleton
const kernelRegistry = KernelRegistry.getInstance()

/**
 * Регистрация ядра в глобальном реестре
 * Вызывается в entry points
 */
export function registerKernel(kernel: KernelLike): void {
    kernelRegistry.registerKernel(kernel)
}

/**
 * Отримання поточного екземпляру ядра
 * Використовується в усіх частинах додатку
 */
export function getKernel(): KernelLike {
    const kernel = kernelRegistry.getKernel()

    if (!kernel) {
        throw new Error(
            'Kernel не зареєстровано в глобальному реєстрі. ' +
                'Переконайтесь, що registerKernel() викликається в entry point.'
        )
    }

    return kernel
}

/**
 * Перевірка доступності ядра
 */
export function isKernelAvailable(): boolean {
    return kernelRegistry.isKernelRegistered()
}

/**
 * Лінивє отримання ядра (повертає null якщо не зареєстровано)
 */
export function getKernelSafe(): KernelLike | null {
    return kernelRegistry.getKernel()
}

/**
 * Допоміжні функції для швидкого доступу до часто використовуваних методів
 */

export function getKernelService<T = unknown>(name: string): T | undefined {
    const kernel = getKernel()
    return kernel.getService ? kernel.getService<T>(name) : undefined
}

export function getKernelStore<T = unknown>(name: string): T | undefined {
    const kernel = getKernel()
    return kernel.getStore ? kernel.getStore<T>(name) : undefined
}

export function getKernelLoader(name: string): any {
    const kernel = getKernel()
    return kernel.getLoader ? kernel.getLoader(name) : undefined
}

export function getKernelConfig(): any {
    const kernel = getKernel()
    return kernel.getConfig ? kernel.getConfig() : undefined
}

export function getKernelRoutes(): any {
    const kernel = getKernel()
    return kernel.getRoutes ? kernel.getRoutes() : undefined
}

/**
 * Встановлення layout store в реєстр
 */
export function setKernelLayoutStore(store: any): void {
    kernelRegistry.setLayoutStore(store)
}

/**
 * Отримання layout store з реєстру
 */
export function getKernelLayoutStore(): any {
    return kernelRegistry.getLayoutStore()
}

/**
 * Скидання реєстру (для тестів)
 */
export function resetKernelRegistry(): void {
    kernelRegistry.reset()

    // Очищуємо з глобального контексту
    if (typeof window !== 'undefined') {
        delete window.__FRONTEND_KERNEL__
    }
}

// Експорт типів
export type { KernelLike }
